import dayjs from "dayjs"
import { formatParticipantName, listParticipants } from "./creditReportV2Utils"

export const CREDIT_REPORT_KEY_PREFIX = "credit-report:"
export const SUBJECT_MORTGAGE_PREFIX = "subject-mortgage:"
export const PROPERTY_MORTGAGE_PREFIX = "property-mortgage:"
export const PROPERTY_ACCOUNT_TYPES = ["Mortgage"]

export function isCreditImported2cLoan(loan) {
    const key = `${loan?.key || ""}`
    return key.startsWith(CREDIT_REPORT_KEY_PREFIX)
        && !key.startsWith(SUBJECT_MORTGAGE_PREFIX)
        && !key.startsWith(PROPERTY_MORTGAGE_PREFIX)
}

export function isCreditImportedSection3Loan(loan) {
    const key = `${loan?.key || ""}`
    return key.includes(CREDIT_REPORT_KEY_PREFIX)
        && (key.startsWith(SUBJECT_MORTGAGE_PREFIX) || key.startsWith(PROPERTY_MORTGAGE_PREFIX))
}

export function getSectionLoansForImportCheck({
    sectionType,
    application,
    participantPartyId,
    reoIndex = 0,
    isSubjectProperty = false,
}) {
    if (sectionType === "section2c") {
        return application?.finance?.liabilitiesByPartyId?.[participantPartyId]?.loans || []
    }
    if (sectionType === "section3") {
        if (isSubjectProperty) {
            return application?.property?.loans || []
        }
        const reo = application?.finance?.realEstateByPartyId?.[participantPartyId]?.properties || []
        if (reoIndex >= 0 && reoIndex < reo.length) {
            return reo[reoIndex]?.loans || []
        }
        return []
    }
    return []
}

export function hasCreditImportedInSection({
    sectionType,
    application,
    participantPartyId,
    reoIndex = 0,
    isSubjectProperty = false,
}) {
    const loans = getSectionLoansForImportCheck({
        sectionType,
        application,
        participantPartyId,
        reoIndex,
        isSubjectProperty,
    })
    const checker = sectionType === "section2c" ? isCreditImported2cLoan : isCreditImportedSection3Loan
    return loans.some(checker)
}

export function buildCreditImportButtonMeta({
    sectionType,
    application,
    participantPartyId,
    reoIndex = 0,
    isSubjectProperty = false,
    hasAvailableCreditReport = false,
    reportCreatedAt = "",
}) {
    const hasImported = hasCreditImportedInSection({
        sectionType,
        application,
        participantPartyId,
        reoIndex,
        isSubjectProperty,
    })

    if (hasImported) {
        const formattedDate = reportCreatedAt ? dayjs(reportCreatedAt).format("MM/DD/YYYY") : ""
        return {
            disabled: false,
            imported: true,
            tooltip: formattedDate
                ? `Last imported from credit report on ${formattedDate}`
                : "Liabilities imported from credit report. Click to review or re-import.",
        }
    }
    if (hasAvailableCreditReport) {
        return {
            disabled: false,
            imported: false,
            tooltip: "View credit report and import liabilities into 1003.",
        }
    }
    return {
        disabled: true,
        imported: false,
        tooltip: "Credit report hasn't been pulled yet. Pull credit first to enable import.",
    }
}

const LOAN_TYPE_TO_PRODUCT = {
    ConventionalRealEstateMortgage: "ConventionalRealEstateMortgage",
    FHARealEstateMortgage: "FHARealEstateMortgage",
    VeteransAdministrationRealEstateMortgage: "VeteransAdministrationRealEstateMortgage",
    "USDA-RD": "USDA-RD",
    Other: "Other",
}

const LOAN_TYPE_LABEL_TO_PRODUCT = {
    Conventional: "ConventionalRealEstateMortgage",
    FHA: "FHARealEstateMortgage",
    VA: "VeteransAdministrationRealEstateMortgage",
    USDA: "USDA-RD",
    Other: "Other",
}

export function normalizeSSN(ssn) {
    return `${ssn || ""}`.replace(/-/g, "").trim()
}

export function sectionLabelForOwned(index, isPurchase) {
    if (isPurchase) {
        if (index === 0) return "3a"
        if (index === 1) return "3b"
        if (index === 2) return "3c"
        return `Extra ${index - 2}`
    }
    if (index === 0) return "3b"
    if (index === 1) return "3c"
    return `Extra ${index - 1}`
}

export function resolveReportContext(report, application) {
    const borrowers = report?.borrowers || []
    const participants = listParticipants(application)
    const primaryPartyId = application?.primaryPartyId || application?.participantOrder?.[0] || ""
    const cohabitingPartyId = `${application?.cohabitingPartyId || ""}`.trim()

    const participantBySSN = {}
    participants.forEach((p) => {
        const ssn = normalizeSSN(p.socialSecurity)
        if (ssn) participantBySSN[ssn] = p
    })

    const reportSSNs = borrowers
        .map((b) => normalizeSSN(b.ssn || b.SSN))
        .filter(Boolean)

    if (borrowers.length === 1) {
        const party = participantBySSN[reportSSNs[0]]
        if (!party) {
            return { error: "Unable to match the credit report subject to a participant on this loan." }
        }
        return {
            pullMode: "individual",
            subjectPartyIds: [party.partyId],
        }
    }

    if (borrowers.length === 2) {
        const primarySSN = normalizeSSN(participants.find((p) => p.partyId === primaryPartyId)?.socialSecurity)
        const spouseSSN = normalizeSSN(participants.find((p) => p.partyId === cohabitingPartyId)?.socialSecurity)
        const expected = new Set([primarySSN, spouseSSN].filter(Boolean))
        const got = new Set(reportSSNs)
        const isSpousePair = cohabitingPartyId
            && expected.size === 2
            && got.size === 2
            && [...expected].every((ssn) => got.has(ssn))

        if (isSpousePair) {
            return {
                pullMode: "joint",
                subjectPartyIds: [primaryPartyId, cohabitingPartyId].filter(Boolean),
            }
        }
        return { error: "This joint report does not match the primary borrower and spouse on file." }
    }

    return { error: "Unsupported credit report subject count." }
}

const toCreditFieldString = (value) => {
    if (value == null || value === "") return ""
    return `${value}`
}

export const mapAccountStatusToPaidOff = (accountStatus) => {
    if (!accountStatus) return "no"
    const status = `${accountStatus}`.toLowerCase()
    if (status === "paid" || status === "closed") return "yes"
    return "no"
}

const normalizeAccountType = (accountType) => (
    (accountType === "Other" || accountType === "CreditLine") ? "Unknown" : accountType
)

export function mapLoanTypeToProductType(loanType) {
    const raw = `${loanType || ""}`.trim()
    if (LOAN_TYPE_TO_PRODUCT[raw]) return LOAN_TYPE_TO_PRODUCT[raw]
    const labelKey = Object.keys(LOAN_TYPE_LABEL_TO_PRODUCT).find((label) => raw.includes(label))
    if (labelKey) return LOAN_TYPE_LABEL_TO_PRODUCT[labelKey]
    return "ConventionalRealEstateMortgage"
}

export function mapCreditRowOwnership(row, reportContext, application) {
    const raw = `${row.ownership || row.creditliabilityShownOwnershipType || ""}`.trim()
    const { pullMode, subjectPartyIds } = reportContext
    const primaryPartyId = application?.primaryPartyId || application?.participantOrder?.[0] || ""
    const cohabitingPartyId = `${application?.cohabitingPartyId || ""}`.trim()

    if (pullMode === "individual") {
        const partyId = subjectPartyIds[0]
        return {
            ownership: partyId === primaryPartyId ? "Borrower" : "CoBorrower",
            ownerPartyIds: [partyId].filter(Boolean),
        }
    }

    if (raw === "Joint") {
        return { ownership: "Joint", ownerPartyIds: [...subjectPartyIds] }
    }
    if (raw === "Borrower") {
        return { ownership: "Borrower", ownerPartyIds: [primaryPartyId].filter(Boolean) }
    }
    if (raw === "CoBorrower") {
        return { ownership: "CoBorrower", ownerPartyIds: [cohabitingPartyId].filter(Boolean) }
    }
    return { ownership: "Joint", ownerPartyIds: [...subjectPartyIds] }
}

function participantDisplayName(participant) {
    if (!participant) return ""
    const parts = [participant.firstName, participant.middleName, participant.lastName, participant.suffix].filter(Boolean)
    return parts.length ? parts.join(" ") : ""
}

function partyNameById(application, partyId) {
    const participants = listParticipants(application)
    const party = participants.find((p) => p.partyId === partyId)
    return participantDisplayName(party) || "--"
}

/**
 * Soft context for Ownership UI when strict SSN pairing fails (demo / missing SSN).
 * Prefer resolveReportContext; fall back to Primary/Secondary → primary/spouse for 2-subject reports.
 */
export function resolveOwnershipReportContext(report, application) {
    const strict = resolveReportContext(report, application)
    if (!strict.error) return strict

    const borrowers = report?.borrowers || []
    const primaryPartyId = application?.primaryPartyId || application?.participantOrder?.[0] || ""
    const cohabitingPartyId = `${application?.cohabitingPartyId || ""}`.trim()
    const participants = listParticipants(application)

    if (borrowers.length === 1) {
        const ssn = normalizeSSN(borrowers[0]?.ssn || borrowers[0]?.SSN)
        const bySsn = ssn
            ? participants.find((p) => normalizeSSN(p.socialSecurity) === ssn)
            : null
        if (bySsn) {
            return { pullMode: "individual", subjectPartyIds: [bySsn.partyId] }
        }
        return strict
    }

    if (borrowers.length === 2 && primaryPartyId && cohabitingPartyId) {
        return {
            pullMode: "joint",
            subjectPartyIds: [primaryPartyId, cohabitingPartyId],
        }
    }
    return strict
}

/** Display label for Ownership column / Review (names, not Borrower/Co-borrower). */
export function formatCreditOwnershipLabel(row, reportContext, application) {
    if (!reportContext || reportContext.error) {
        const raw = `${row?.ownership || ""}`.trim()
        if (raw === "CoBorrower") return "Co-borrower"
        return raw || "--"
    }
    const { ownership, ownerPartyIds } = mapCreditRowOwnership(row || {}, reportContext, application)
    if (ownership === "Joint" || ownerPartyIds.length > 1) {
        const names = ownerPartyIds.map((id) => partyNameById(application, id)).filter((n) => n && n !== "--")
        if (names.length >= 2) return `${names[0]} & ${names[1]}`
        if (names.length === 1) return names[0]
        return "Joint"
    }
    const partyId = ownerPartyIds[0]
    return partyId ? partyNameById(application, partyId) : "--"
}

/**
 * Select options for editable Ownership (Joint reports).
 * Values remain Borrower | CoBorrower | Joint for updateReport.
 * Individual: empty list (caller should set ownershipEditable=false).
 */
export function buildCreditOwnershipOptions(reportContext, application) {
    if (!reportContext || reportContext.error || reportContext.pullMode !== "joint") {
        return []
    }
    const primaryPartyId = application?.primaryPartyId || application?.participantOrder?.[0] || ""
    const cohabitingPartyId = `${application?.cohabitingPartyId || ""}`.trim()
    const primaryName = partyNameById(application, primaryPartyId)
    const spouseName = partyNameById(application, cohabitingPartyId)
    const options = []
    if (primaryPartyId) {
        options.push({ value: "Borrower", label: primaryName })
    }
    if (cohabitingPartyId) {
        options.push({ value: "CoBorrower", label: spouseName })
    }
    if (primaryPartyId && cohabitingPartyId) {
        options.push({
            value: "Joint",
            label: `${primaryName} & ${spouseName}`,
        })
    }
    return options
}

export function isCreditOwnershipEditable(reportContext) {
    return !!(reportContext && !reportContext.error && reportContext.pullMode === "joint")
}

export function mapCreditLiabilityForImport(row, index, report, reportContext, application) {
    const accountType = normalizeAccountType(row.accountType)
    const loanType = row.loanType || accountType || "Installment"
    const { ownership, ownerPartyIds } = mapCreditRowOwnership(row, reportContext, application)
    const accountKey = row.accountIdentifier || row.id || index

    return {
        key: `${CREDIT_REPORT_KEY_PREFIX}${report.id}:${accountKey}`,
        creditLoanType: accountType || loanType,
        openedDate: toCreditFieldString(row.openedDate || ""),
        name: row.creditorName || row.name || "",
        address: row.address || "",
        accountNumber: toCreditFieldString(row.accountIdentifier || row.accountNumber || ""),
        monthlyPayment: toCreditFieldString(Math.floor(Number(row.monthlyPaymentAmount || 0))),
        remainingBalance: toCreditFieldString(Math.floor(Number(row.unpaidBalanceAmount || 0))),
        highBalance: toCreditFieldString(row.highBalanceAmount ?? ""),
        monthsLeftToPay: toCreditFieldString(row.monthsRemainingCount ?? ""),
        ownership,
        ownerPartyIds,
        satisfiedUpon: "not",
        paidOffAtClosing: mapAccountStatusToPaidOff(row.accountStatus),
        rate: "",
        correction: "none",
        explanation: "",
        propertyAddress: row.propertyAddress || "",
        creditLimit: "",
    }
}

export function mapCreditMortgageForImport(row, index, report, reportContext, application, linkTarget) {
    const { ownership, ownerPartyIds } = mapCreditRowOwnership(row, reportContext, application)
    const accountKey = row.accountIdentifier || row.id || index
    const baseKey = `${CREDIT_REPORT_KEY_PREFIX}${report.id}:${accountKey}`
    const prefix = linkTarget.target === "subject" ? "subject-mortgage:" : "property-mortgage:"

    return {
        key: `${prefix}${baseKey}`,
        creditLoanType: mapLoanTypeToProductType(row.loanType),
        name: row.creditorName || row.name || "",
        accountNumber: toCreditFieldString(row.accountIdentifier || row.accountNumber || ""),
        monthlyPayment: toCreditFieldString(Math.floor(Number(row.monthlyPaymentAmount || 0))),
        remainingBalance: toCreditFieldString(Math.floor(Number(row.unpaidBalanceAmount || 0))),
        ownership,
        ownerPartyIds,
        propertyAddress: linkTarget.address,
        paidOffAtClosing: mapAccountStatusToPaidOff(row.accountStatus),
        forSubjectProperty: linkTarget.target === "subject",
    }
}

/**
 * Section 3 properties that can be linked to credit mortgages.
 * When subjectPartyIds is non-empty, only include those parties' properties
 * (and subject/3a only if the primary party is among them).
 */
export function listLinkableProperties(application, subjectPartyIds) {
    const participants = listParticipants(application)
    const isPurchase = application?.property?.purpose === "purchase"
    const allowed = Array.isArray(subjectPartyIds) && subjectPartyIds.length > 0
        ? new Set(subjectPartyIds.filter(Boolean).map((id) => `${id}`))
        : null
    const properties = []

    if (!isPurchase && `${application?.property?.address || ""}`.trim()) {
        const primaryPartyId = application?.primaryPartyId || application?.participantOrder?.[0] || ""
        if (!allowed || allowed.has(`${primaryPartyId}`)) {
            const primary = participants.find((p) => p.partyId === primaryPartyId) || participants[0]
            properties.push({
                linkId: `subject:${primaryPartyId}`,
                partyId: primaryPartyId,
                target: "subject",
                propertyIndex: -1,
                address: application.property.address,
                shortAddress: `${application.property.address}`.split(",")[0],
                section: "3a",
                sectionDescription: "Section 3a. Property you own",
                participantName: formatParticipantName(primary),
            })
        }
    }

    participants.forEach((participant) => {
        if (allowed && !allowed.has(`${participant.partyId}`)) return
        const reo = application?.finance?.realEstateByPartyId?.[participant.partyId]?.properties || []
        reo.forEach((prop, index) => {
            const address = `${prop.address || prop.street || ""}`.trim()
            if (!address) return
            const section = sectionLabelForOwned(index, isPurchase)
            properties.push({
                linkId: `owned:${participant.partyId}:${index}`,
                partyId: participant.partyId,
                target: "owned",
                propertyIndex: index,
                address,
                shortAddress: address.split(",")[0],
                section,
                sectionDescription: `Section ${section}. ${address}`,
                participantName: formatParticipantName(participant),
            })
        })
    })

    return properties
}

function cloneRealEstateByPartyId(application) {
    const out = {}
    listParticipants(application).forEach((participant) => {
        const source = application?.finance?.realEstateByPartyId?.[participant.partyId]?.properties || []
        out[participant.partyId] = {
            properties: JSON.parse(JSON.stringify(source)),
        }
    })
    return out
}

function stripReportCreditLoans(loans, reportId, keyPrefix) {
    const needle = `${CREDIT_REPORT_KEY_PREFIX}${reportId}:`
    return (loans || []).filter((loan) => {
        const key = `${loan?.key || ""}`
        if (!key.includes(needle)) return true
        if (keyPrefix && !key.startsWith(keyPrefix)) return true
        return false
    })
}

export function buildPropertyImportSnapshots(application, report, reportContext, mortgageLinks) {
    const reportId = report.id
    const isPurchase = application?.property?.purpose === "purchase"
    let subjectLoans = [...(application?.property?.loans || [])]
    const realEstateByPartyId = cloneRealEstateByPartyId(application)
    const linkable = listLinkableProperties(application)
    const touchedSubject = new Set()
    const touchedParties = new Set()

    subjectLoans = stripReportCreditLoans(subjectLoans, reportId, "subject-mortgage:")
    Object.keys(realEstateByPartyId).forEach((partyId) => {
        realEstateByPartyId[partyId].properties = (realEstateByPartyId[partyId].properties || []).map((prop) => ({
            ...prop,
            loans: stripReportCreditLoans(prop.loans, reportId, "property-mortgage:"),
        }))
    })

    ;(mortgageLinks || []).forEach(({ row, linkIds }, index) => {
        (linkIds || []).forEach((linkId) => {
            const target = linkable.find((item) => item.linkId === linkId)
            if (!target) return
            const loan = mapCreditMortgageForImport(row, index, report, reportContext, application, target)
            if (target.target === "subject") {
                subjectLoans = [...subjectLoans, loan]
                touchedSubject.add("subject")
            } else {
                touchedParties.add(target.partyId)
                const props = realEstateByPartyId[target.partyId]?.properties || []
                const current = props[target.propertyIndex] || { address: target.address }
                props[target.propertyIndex] = {
                    ...current,
                    address: current.address || target.address,
                    loans: [...(current.loans || []), loan],
                    ownedFreeAndClear: false,
                }
                realEstateByPartyId[target.partyId] = { properties: props }
            }
        })
    })

    const result = {
        importSubjectMortgage: false,
        subjectPropertyLoans: [],
        realEstateByPartyId: {},
    }

    if (!isPurchase && touchedSubject.size > 0) {
        result.importSubjectMortgage = true
        result.subjectPropertyLoans = subjectLoans
    }

    touchedParties.forEach((partyId) => {
        result.realEstateByPartyId[partyId] = realEstateByPartyId[partyId]
    })

    return result
}

/** Mirrors Go creditImportHomePartyID: where section 2c liabilities land in 1003. */
export function resolveCreditImportHomePartyId(row, reportContext, application) {
    const primaryPartyId = application?.primaryPartyId || application?.participantOrder?.[0] || ""
    const { ownerPartyIds } = mapCreditRowOwnership(row, reportContext, application)
    const filtered = (ownerPartyIds || []).filter(Boolean)
    if (filtered.length >= 2) {
        if (filtered.includes(primaryPartyId)) return primaryPartyId
        return filtered[0]
    }
    if (filtered.length === 1) return filtered[0]
    return reportContext?.subjectPartyIds?.[0] || primaryPartyId
}

function resolveCreditImportTargetPartyIdWithContext(reportContext, application, selectedRows, selectedLinks) {
    const primaryPartyId = application?.primaryPartyId || application?.participantOrder?.[0] || ""
    const section2cRows = (selectedRows || []).filter(
        (row) => !PROPERTY_ACCOUNT_TYPES.includes(row.accountType),
    )
    const section3Rows = (selectedRows || []).filter(
        (row) => PROPERTY_ACCOUNT_TYPES.includes(row.accountType),
    )

    if (section2cRows.length > 0) {
        const homeParties = new Set()
        section2cRows.forEach((row) => {
            const id = resolveCreditImportHomePartyId(row, reportContext, application)
            if (id) homeParties.add(id)
        })
        if (homeParties.size === 1) return [...homeParties][0]
        if (homeParties.size > 1) return primaryPartyId
    }

    if (section3Rows.length > 0) {
        const linkableById = {}
        listLinkableProperties(application).forEach((property) => {
            linkableById[property.linkId] = property
        })
        const parties = new Set()
        section3Rows.forEach((row, index) => {
            const key = `${row.key || row.accountIdentifier || row.id || row.creditorName || "row"}:${index}`
            ;(selectedLinks[key] || []).forEach((linkId) => {
                const property = linkableById[linkId]
                if (property?.partyId) parties.add(property.partyId)
            })
        })
        if (parties.size === 1) return [...parties][0]
        if (parties.size > 1) return primaryPartyId
    }

    const subjects = reportContext?.subjectPartyIds || []
    if (subjects.length === 1) return subjects[0]
    return primaryPartyId
}

/** Participant tab to focus after credit import navigation. */
export function resolveCreditImportTargetPartyId(report, application, selectedRows, selectedLinks = {}) {
    if (!application) return ""
    const primaryPartyId = application?.primaryPartyId || application?.participantOrder?.[0] || ""
    if (!report || !selectedRows?.length) return primaryPartyId

    const strict = resolveReportContext(report, application)
    if (!strict.error) {
        return resolveCreditImportTargetPartyIdWithContext(strict, application, selectedRows, selectedLinks)
    }
    const soft = resolveOwnershipReportContext(report, application)
    if (soft.error) return primaryPartyId
    return resolveCreditImportTargetPartyIdWithContext(soft, application, selectedRows, selectedLinks)
}

export function resolveCreditReportNavigationPartyId(report, application) {
    if (!application) return ""
    const primaryPartyId = application?.primaryPartyId || application?.participantOrder?.[0] || ""
    if (!report) return primaryPartyId

    const context = resolveOwnershipReportContext(report, application)
    if (context.error) return primaryPartyId
    const subjects = context.subjectPartyIds || []
    if (subjects.length === 1) return subjects[0]
    return primaryPartyId
}

export function buildCreditImportPayload(report, application, selectedRows, mortgageLinks = []) {
    const reportContext = resolveReportContext(report, application)
    if (reportContext.error) {
        throw new Error(reportContext.error)
    }

    const section2cRows = (selectedRows || []).filter(
        (row) => !PROPERTY_ACCOUNT_TYPES.includes(row.accountType),
    )
    const section3Rows = (selectedRows || []).filter(
        (row) => PROPERTY_ACCOUNT_TYPES.includes(row.accountType),
    )

    const loans = section2cRows.map((row, index) =>
        mapCreditLiabilityForImport(row, index, report, reportContext, application),
    )

    const propertySnapshots = section3Rows.length > 0
        ? buildPropertyImportSnapshots(application, report, reportContext, mortgageLinks)
        : { importSubjectMortgage: false, subjectPropertyLoans: [], realEstateByPartyId: {} }

    return {
        reportId: report.id,
        pullMode: reportContext.pullMode,
        subjectPartyIds: reportContext.subjectPartyIds,
        loans,
        importSubjectMortgage: propertySnapshots.importSubjectMortgage,
        subjectPropertyLoans: propertySnapshots.subjectPropertyLoans,
        realEstateByPartyId: propertySnapshots.realEstateByPartyId,
    }
}

export function buildMortgageLinkRows(section3Rows, selectedLinkByRowKey) {
    return (section3Rows || []).map((row, index) => {
        const rowKey = `${row.key || row.accountIdentifier || row.id || row.creditorName || "row"}:${index}`
        return {
            row,
            linkIds: selectedLinkByRowKey[rowKey] || [],
        }
    }).filter((item) => item.linkIds.length > 0)
}
